import type { CatalogSizingOption } from '@/services/iem/types';
import type {
  IssueAdditionalItemDisplayRow,
  IssueAdditionalItemRow,
} from './IssueAdditionalItemsTable';

export type AvailabilityFilter = 'all' | 'available' | 'unavailable';

const matchesAvailabilityFilter = (
  row: IssueAdditionalItemRow,
  filter: AvailabilityFilter,
): boolean => {
  if (filter === 'all') return true;
  if (filter === 'available') return row.availableQty > 0;
  return row.availableQty === 0;
};

const tokenizeSearch = (value: string): string[] =>
  value
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((token) => token.length > 0);

const matchesSearchTokens = (row: IssueAdditionalItemRow, tokens: string[]): boolean => {
  if (tokens.length === 0) return true;

  const combined = [row.name, row.sizeLabel, row.lin, row.nsn]
    .filter((value): value is string => Boolean(value && value.trim().length > 0))
    .join(' ')
    .toLowerCase();

  return tokens.every((token) => combined.includes(token));
};

type RootNode =
  | {
      type: 'parent';
      parentId: string;
      parentRow: IssueAdditionalItemRow;
      children: IssueAdditionalItemRow[];
    }
  | { type: 'leaf'; row: IssueAdditionalItemRow };

export const buildAdditionalItemsDisplayRows = ({
  rowsByCatalogId,
  catalogChildren,
  expandedParentIds,
  search,
  availabilityFilter = 'all',
}: {
  rowsByCatalogId: Map<string, IssueAdditionalItemRow>;
  catalogChildren: CatalogSizingOption[];
  expandedParentIds: Set<string>;
  search: string;
  availabilityFilter?: AvailabilityFilter;
}): IssueAdditionalItemDisplayRow[] => {
  const tokens = tokenizeSearch(search);
  const hasSearch = tokens.length > 0;

  const childrenByParentId = new Map<string, CatalogSizingOption[]>();
  catalogChildren.forEach((child) => {
    const siblings = childrenByParentId.get(child.parentCatalogId);
    if (siblings) {
      siblings.push(child);
    } else {
      childrenByParentId.set(child.parentCatalogId, [child]);
    }
  });

  childrenByParentId.forEach((children) => {
    children.sort((a, b) =>
      a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' }),
    );
  });

  const childCatalogIds = new Set<string>();
  childrenByParentId.forEach((children) => {
    children.forEach((child) => childCatalogIds.add(child.id));
  });

  const roots: RootNode[] = [];

  childrenByParentId.forEach((children, parentId) => {
    // Only process children whose parent exists in this workflow's catalog data.
    // getAllCatalogChildren returns all catalog children globally, so we must
    // exclude any that belong to unrelated parents.
    if (!rowsByCatalogId.has(parentId)) return;

    const parentRow = rowsByCatalogId.get(parentId)!;

    const childRows = children.map((child) => {
      const fromInventory = rowsByCatalogId.get(child.id);
      if (fromInventory) {
        return {
          ...fromInventory,
          sizeLabel: child.sizeLabel ?? fromInventory.sizeLabel ?? null,
        };
      }

      let name = child.displayName;
      const sizeMatch = name.match(/^(.*)\s+\(([^)]+)\)$/);
      if (sizeMatch) {
        name = sizeMatch[1]!.trim();
      }

      return {
        groupId: child.id,
        lin: child.lin ?? null,
        nsn: child.nsn ?? null,
        name,
        sizeLabel: child.sizeLabel ?? null,
        availableQty: 0,
        availableAssetIds: [],
      };
    });

    const totalAvailability = childRows.reduce((sum, row) => sum + row.availableQty, 0);
    const resolvedParentRow: IssueAdditionalItemRow = {
      ...parentRow,
      availableQty: Math.max(totalAvailability, parentRow.availableQty),
    };

    roots.push({ type: 'parent', parentId, parentRow: resolvedParentRow, children: childRows });
  });

  rowsByCatalogId.forEach((row, catalogId) => {
    if (childrenByParentId.has(catalogId)) {
      return;
    }
    if (childCatalogIds.has(catalogId)) {
      return;
    }
    roots.push({ type: 'leaf', row });
  });

  const sortedRoots = [...roots].sort((a, b) => {
    const leftName = (a.type === 'leaf' ? a.row.name : a.parentRow.name).trim();
    const rightName = (b.type === 'leaf' ? b.row.name : b.parentRow.name).trim();
    const nameCompare = leftName.localeCompare(rightName, undefined, { sensitivity: 'base' });
    if (nameCompare !== 0) {
      return nameCompare;
    }
    const leftLin = a.type === 'leaf' ? (a.row.lin ?? '') : (a.parentRow.lin ?? '');
    const rightLin = b.type === 'leaf' ? (b.row.lin ?? '') : (b.parentRow.lin ?? '');
    return leftLin.localeCompare(rightLin, undefined, { sensitivity: 'base' });
  });

  const flattened: IssueAdditionalItemDisplayRow[] = [];

  const hasAvailabilityFilter = availabilityFilter !== 'all';

  sortedRoots.forEach((root) => {
    if (root.type === 'leaf') {
      if (!matchesSearchTokens(root.row, tokens)) {
        return;
      }
      if (!matchesAvailabilityFilter(root.row, availabilityFilter)) {
        return;
      }
      flattened.push({
        ...root.row,
        depth: 0,
        hasChildren: false,
        isExpanded: false,
      });
      return;
    }

    const parentMatches = matchesSearchTokens(root.parentRow, tokens);
    const matchingChildren = root.children.filter(
      (child) =>
        matchesSearchTokens(child, tokens) &&
        matchesAvailabilityFilter(child, availabilityFilter),
    );
    const shouldIncludeParent =
      (!hasSearch || parentMatches || matchingChildren.length > 0) &&
      (!hasAvailabilityFilter || matchingChildren.length > 0);

    if (!shouldIncludeParent) {
      return;
    }

    const autoExpand = hasSearch && (parentMatches || matchingChildren.length > 0);
    const isExpanded = expandedParentIds.has(root.parentId) || autoExpand;

    flattened.push({
      ...root.parentRow,
      depth: 0,
      hasChildren: true,
      isExpanded,
    });

    if (!isExpanded) {
      return;
    }

    const childrenToShow =
      hasSearch || hasAvailabilityFilter
        ? parentMatches && !hasAvailabilityFilter
          ? root.children
          : matchingChildren
        : root.children;

    childrenToShow.forEach((child) => {
      flattened.push({
        ...child,
        depth: 1,
        hasChildren: false,
        isExpanded: false,
      });
    });
  });

  return flattened;
};
